import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { modeLabel, type GameMode } from '@shared/chart';
import { getNcsSong, relatedNcsSongs } from '@shared/seo/ncsSongCatalogue';
import {
  buildCanonical,
  SITE_NAME,
  songHubDetailMeta,
  type RouteMeta,
} from '@shared/seo/seoMeta';
import { useDocumentHead } from '../seo/useDocumentHead';
import { saveModePref } from '../game/modePref';
import { modePath } from '../game/modeRouting';
import { TrademarkDisclaimer } from './TrademarkDisclaimer';
import './songsHub.css';

/**
 * Short genre pitch per available mode.
 * Describes the controls and feel of each rhythm game.
 */
const MODE_PITCH: Record<GameMode, string> = {
  circles: 'osu-inspired circle tapping',
  piano: 'Piano Tiles-inspired tiles',
  guitar: 'Guitar Hero / Clone Hero-inspired fretboard',
  waves: 'Geometry-Dash-inspired wave flight',
  jump: 'Geometry-Dash-inspired cube jumps',
  pulse: 'one-button gravity runner',
};

/**
 * Tier-3 per-song hub page (`/songs/:slug`): a single track's facts
 * (artist, tempo, difficulty, official release link), play CTAs for its available
 * modes, and a "songs like it" list ranked by real tempo / difficulty / artist
 * similarity. The page owns its own head — the title needs the song record —
 * so it calls {@link useDocumentHead} directly (the client route resolver
 * returns `null` for `/songs/:slug`, mirroring song play pages).
 */
export function SongDetailPage() {
  const { slug = '' } = useParams<{ slug: string }>();
  const song = getNcsSong(slug);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const meta: RouteMeta = song
    ? songHubDetailMeta({
        slug: song.baseSlug,
        title: song.title,
        artist: song.artist,
        displayBpm: song.displayBpm,
      })
    : {
        path: `/songs/${slug}`,
        title: `Track not found | ${SITE_NAME}`,
        description: `That track isn't in the ${SITE_NAME} catalogue.`,
        canonical: buildCanonical(`/songs/${slug}`),
        noindex: true,
      };
  useDocumentHead(meta);

  if (!song) {
    return (
      <div className="songs-hub song-detail" data-testid="song-detail-missing">
        <p className="muted">That track isn&apos;t in our catalogue.</p>
        <Link to="/songs" className="btn">
          Back to all songs
        </Link>
      </div>
    );
  }

  const related = relatedNcsSongs(song.baseSlug, 3);

  return (
    <div className="songs-hub song-detail" data-testid={`song-detail-${song.baseSlug}`}>
      <nav className="song-detail-crumbs" aria-label="Breadcrumb">
        <Link to="/songs">← All songs</Link>
      </nav>

      <header className="song-detail-header">
        <h1>
          {song.title}{' '}
          <span className="song-detail-artist"> by {song.artist}</span>
        </h1>
        <p className="song-detail-lede">
          {song.description} Play it free in your browser at {SITE_NAME} — no
          download, and unblocked at school.
        </p>
        <div className="song-detail-facts">
          <span className="song-detail-fact">{song.displayBpm} BPM</span>
          <span className="song-detail-fact">Difficulty {song.difficulty}/5</span>
          <a
            className="song-detail-fact song-detail-ncs"
            href={song.releaseUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Official {song.label} release <span aria-hidden="true">↗</span>
          </a>
        </div>
      </header>

      <section className="songs-hub-section" aria-labelledby="song-detail-play">
        <h2 id="song-detail-play">Play &ldquo;{song.title}&rdquo;</h2>
        <div className="song-mode-grid">
          {song.playModes.map(({ mode, slug: chartSlug }) => (
            <Link
              key={mode}
              to={modePath(mode, `play/${chartSlug}`)}
              className="song-mode-card"
              data-testid={`song-mode-${mode}`}
              onClick={() => saveModePref(mode)}
            >
              <span className="song-mode-name">{modeLabel(mode)}</span>
              <span className="song-mode-pitch">{MODE_PITCH[mode]}</span>
            </Link>
          ))}
        </div>
      </section>

      {related.length > 0 && (
        <section className="songs-hub-section" aria-labelledby="song-detail-related">
          <h2 id="song-detail-related">Songs like {song.title}</h2>
          <p className="muted">
            More tracks with a similar tempo and intensity.
          </p>
          <ul className="song-related-list">
            {related.map((r) => (
              <li key={r.baseSlug}>
                <Link
                  to={`/songs/${r.baseSlug}`}
                  className="song-related-card"
                  data-testid={`song-related-${r.baseSlug}`}
                >
                  <span className="song-related-title">{r.title}</span>
                  <span className="song-related-artist">{r.artist}</span>
                  <span className="song-related-meta">
                    {r.displayBpm} BPM · Difficulty {r.difficulty}/5
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <TrademarkDisclaimer />
    </div>
  );
}
